import qs from 'query-string';
import createRequest from './request';
import { getAnonymousId } from '../analytics/utils';
export const createRequestConfig = ({ authToken, orderToken, } = {}) => {
    const config = { headers: {} };
    if (authToken) {
        config.headers.Authorization = authToken;
    }
    else if (orderToken) {
        config.headers['X-Spree-Order-Token'] = orderToken;
    }
    try {
        const anonymousId = getAnonymousId();
        if (anonymousId) {
            config.headers['ACS-Anonymous-Id'] = anonymousId;
        }
    }
    catch (error) {
        console.error(error);
    }
    return config;
};
export const createOMSClient = (omsClientConfig) => {
    const request = createRequest(omsClientConfig);
    const client = {
        createOrder: async (authToken, data) => {
            const url = '/api/orders';
            const defaultData = { lineItemsAttributes: [] };
            const config = createRequestConfig({ authToken });
            const response = await request.post(url, data || defaultData, config);
            return response.data;
        },
        getOrder: async (order, authToken) => {
            const url = `/api/orders/${order.number}`;
            const config = createRequestConfig({ authToken, orderToken: order.token });
            const response = await request.get(url, config);
            return response.data;
        },
        getCurrentOrder: async (authToken) => {
            const url = '/api/orders/current';
            const config = createRequestConfig({ authToken });
            const response = await request.get(url, config);
            return response.data;
        },
        updateOrder: async (order, authToken, data) => {
            const url = `/api/orders/${order.number}`;
            const config = createRequestConfig({ authToken, orderToken: order.token });
            const response = await request.patch(url, data, config);
            return response.data;
        },
        emptyOrder: async (order, authToken) => {
            const url = `/api/orders/${order.number}/empty`;
            const config = createRequestConfig({ authToken, orderToken: order.token });
            await request.put(url, null, config);
            return true;
        },
        addLineItem: async (order, authToken, data) => {
            const url = `/api/orders/${order.number}/line_items`;
            const config = createRequestConfig({ authToken, orderToken: order.token });
            const response = await request.post(url, data, config);
            return response.data;
        },
        removeLineItem: async (order, authToken, lineItemId) => {
            const url = `/api/orders/${order.number}/line_items/${lineItemId}`;
            const config = createRequestConfig({ authToken, orderToken: order.token });
            await request.delete(url, config);
            return undefined;
        },
        updateLineItem: async (order, authToken, lineItemId, data) => {
            const url = `/api/orders/${order.number}/line_items/${lineItemId}`;
            const config = createRequestConfig({ authToken, orderToken: order.token });
            const response = await request.patch(url, data, config);
            return response.data;
        },
        updateGiftCards: async (order, data) => {
            const url = `/api/orders/${order.number}/gift_cards/bulk_update`;
            const config = createRequestConfig({ orderToken: order.token });
            const response = await request.patch(url, data, config);
            return response.data;
        },
        addCouponCode: async (order, authToken, couponCode) => {
            const url = `/api/orders/${order.number}/coupon_codes`;
            const data = { couponCode };
            const config = createRequestConfig({ authToken, orderToken: order.token });
            const response = await request.post(url, data, config);
            return response.data;
        },
        removeCouponCode: async (order, authToken, couponCode) => {
            const url = `/api/orders/${order.number}/coupon_codes/${couponCode}`;
            const config = createRequestConfig({ authToken, orderToken: order.token });
            const response = await request.delete(url, config);
            return response.data;
        },
        prepareCheckout: async (order, authToken) => {
            const url = `/api/checkouts/${order.number}/prepare`;
            const config = createRequestConfig({ authToken, orderToken: order.token });
            const response = await request.put(url, null, config);
            return response.data;
        },
        finalizeCheckout: async (order, authToken, data) => {
            const url = `/api/checkouts/${order.number}/finalize`;
            const config = createRequestConfig({ authToken, orderToken: order.token });
            const response = await request.put(url, data, config);
            return response.data;
        },
        updateCheckout: async (order, authToken, data) => {
            const url = `/api/checkouts/${order.number}`;
            const config = createRequestConfig({ authToken, orderToken: order.token });
            const response = await request.patch(url, data, config);
            return response.data;
        },
        crossSell: async (order, authToken, data) => {
            const url = `/api/orders/${order.number}/cross_sell`;
            const config = createRequestConfig({ authToken, orderToken: order.token });
            const response = await request.put(url, data, config);
            return response.data;
        },
        getReferralIdentifier: async (data) => {
            const url = '/api/referral_identifiers';
            const config = createRequestConfig();
            const response = await request.post(url, data, config);
            return response.data;
        },
        getCountryStates: async (countryId) => {
            const url = `/api/countries/${countryId}/states`;
            const config = createRequestConfig();
            const response = await request.get(url, config);
            return response.data;
        },
        associateOrderWithUser: async (order, authToken) => {
            const url = `/api/orders/${order.number}/associate_user?order_token=${order.token}`;
            const config = createRequestConfig({ authToken });
            const response = await request.put(url, null, config);
            return response.data;
        },
        getCurrentUser: async (authToken) => {
            const url = '/api/users/me';
            const config = createRequestConfig({ authToken });
            const response = await request.get(url, config);
            return response.data;
        },
        getUserWallet: async (authToken) => {
            const url = '/api/wallet_payment_sources';
            const config = createRequestConfig({ authToken });
            const response = await request.get(url, config);
            return response.data;
        },
        getUserOrders: async (authToken, includeDetails, legacyEndpoint, page = 1, perPage = 6) => {
            const url = legacyEndpoint === false ? '/api/orders/completed' : '/api/orders/mine';
            const config = createRequestConfig({ authToken });
            const params = {
                include_details: includeDetails === true ? includeDetails : undefined,
                page: page,
                per_page: perPage,
            };
            const response = await request.get(url, {
                ...config,
                params,
            });
            return response.data;
        },
        getUserAddressBook: async (authToken, userId) => {
            const url = `/api/users/${userId}/address_book`;
            const config = createRequestConfig({ authToken });
            const response = await request.get(url, config);
            return response.data;
        },
        updateUserAddressBook: async (authToken, userId, address) => {
            const url = `/api/users/${userId}/address_book`;
            const config = createRequestConfig({ authToken });
            const response = await request.patch(url, address, config);
            return response.data;
        },
        removeFromUserAddressBook: async (authToken, userId, addressId) => {
            const url = `/api/users/${userId}/address_book?address_id=${addressId}`;
            const config = createRequestConfig({ authToken });
            const response = await request.delete(url, config);
            return response.data;
        },
        getUserStoreCreditEvents: async (authToken) => {
            const url = '/api/store_credit_events/mine';
            const config = createRequestConfig({ authToken });
            const response = await request.get(url, config);
            return response.data;
        },
        updateUser: async (authToken, userId, data) => {
            const url = `/api/users/${userId}`;
            const config = createRequestConfig({ authToken });
            const response = await request.patch(url, { id: userId, ...data }, config);
            return response.data;
        },
        getSubscriptions: async (authToken, userId) => {
            const url = `/api/users/${userId}/subscriptions`;
            const config = createRequestConfig({ authToken });
            const response = await request.get(url, config);
            return response.data;
        },
        getSubscription: async (authToken, userId, subscriptionId) => {
            const url = `/api/users/${userId}/subscriptions/${subscriptionId}`;
            const config = createRequestConfig({ authToken });
            const response = await request.get(url, config);
            return response.data;
        },
        skipSubscription: async (authToken, userId, subscriptionId) => {
            const url = `/api/users/${userId}/subscriptions/${subscriptionId}/skip`;
            const config = createRequestConfig({ authToken });
            const response = await request.post(url, null, config);
            return response.data;
        },
        cancelSubscription: async (authToken, userId, subscriptionId) => {
            const url = `/api/users/${userId}/subscriptions/${subscriptionId}/cancel`;
            const config = createRequestConfig({ authToken });
            const response = await request.post(url, null, config);
            return response.data;
        },
        updateSubscription: async (authToken, userId, subscriptionId, data) => {
            const url = `/api/users/${userId}/subscriptions/${subscriptionId}`;
            const config = createRequestConfig({ authToken });
            const response = await request.put(url, data, config);
            return response.data;
        },
        resumeSubscription: async (authToken, userId, subscriptionId, date) => {
            const url = `/api/users/${userId}/subscriptions/${subscriptionId}/resume`;
            const config = createRequestConfig({ authToken });
            const data = typeof date === 'object' && !(date instanceof Date)
                ? date
                : { actionableDate: date };
            const response = await request.post(url, data, config);
            return response.data;
        },
        pauseSubscription: async (authToken, userId, subscriptionId, date) => {
            const url = `/api/users/${userId}/subscriptions/${subscriptionId}/pause`;
            const config = createRequestConfig({ authToken });
            const data = typeof date === 'object' && !(date instanceof Date)
                ? date
                : { actionableDate: date };
            const response = await request.post(url, data, config);
            return response.data;
        },
        getVariantsBySkus: async (authToken, skus) => {
            const url = '/api/variants';
            const config = {
                ...createRequestConfig({ authToken }),
                params: {
                    'q[sku_in]': skus,
                },
                paramsSerializer: (params) => {
                    return qs.stringify(params, { arrayFormat: 'bracket' });
                },
            };
            const response = await request.get(url, config);
            return response.data;
        },
        createStockRequest: async (data) => {
            const url = '/api/stock_requests';
            const config = createRequestConfig();
            const response = await request.post(url, data, config);
            return response.data;
        },
        redeemGiftCard: async (redemptionCode, email) => {
            const url = '/api/gift_cards/redeem';
            const config = createRequestConfig();
            const response = await request.post(url, { redemptionCode, email }, config);
            return response.data;
        },
        redeemPrepaidSubscription: async (input) => {
            const url = '/api/gift_cards/redeem';
            const config = createRequestConfig();
            const response = await request.post(url, input, config);
            return response.data;
        },
        checkRedemptionCodeValidity: async (redemptionCode, email) => {
            const url = `/api/gift_cards/redeem`;
            const config = {
                ...createRequestConfig(),
                params: {
                    redemption_code: redemptionCode,
                    email: email,
                },
                paramsSerializer: (params) => {
                    return qs.stringify(params, { arrayFormat: 'bracket' });
                },
            };
            const response = await request.get(url, config);
            return response.data;
        },
        getVariantStockAvailability: async ({ sku, ...params }) => {
            const url = `/api/variants/${sku}/stock_availabilities`;
            const config = {
                ...createRequestConfig(),
                params,
            };
            const response = await request.get(url, config);
            return response.data;
        },
        getVariantPrice: async ({ sku, ...params }) => {
            const url = `/api/variants/${sku}/discount_prices`;
            const config = {
                ...createRequestConfig(),
                params: {
                    quantity: params.quantity,
                    interval_length: params.intervalLength,
                    interval_units: params.intervalUnits,
                },
            };
            const response = await request.get(url, config);
            return response.data;
        },
    };
    return client;
};
